/**
 * PidLayerPanel — Autodesk / AVEVA-style layer manager for P&IDs.
 *
 * Fetches the layer catalogue from ``GET /api/pid/layers`` and lets
 * the user:
 *   • toggle visibility per-layer
 *   • freeze (hide + click-through) an entire layer
 *   • recolour a layer for visual grouping
 *   • switch between ISA / AutoCAD Plant 3D / AVEVA naming conventions
 *
 * All actions mutate the rendered SVG in-place via
 * ``[data-layer='…']`` selectors — no re-render needed.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CONVENTIONS = [
  { key: "isa",      label: "ISO 128 / ISA-5.1" },
  { key: "plant3d",  label: "AutoCAD Plant 3D"  },
  { key: "aveva",    label: "AVEVA Diagrams"    },
];

const CATEGORY_ORDER = [
  "structural", "mechanical", "piping", "valves",
  "instrumentation", "electrical", "safety", "annotation",
];

const CATEGORY_LABELS = {
  structural:       "Structural / Furniture",
  mechanical:       "Mechanical / Equipment",
  piping:           "Piping",
  valves:           "Valves & Specialty",
  instrumentation:  "Instrumentation",
  electrical:       "Electrical",
  safety:           "Safety (SIS / HAZOP)",
  annotation:       "Annotations",
};

export const PidLayerPanel = ({ svgContainerRef, onConventionChange }) => {
  const [convention, setConvention] = useState("isa");
  const [layers, setLayers] = useState([]);
  const [state,  setState]  = useState({});      // {layer_id: {visible, frozen, color}}

  // ─── Fetch layer catalogue ───────────────────────────────
  useEffect(() => {
    axios
      .get(`${API}/api/pid/layers`, { params: { convention } })
      .then((r) => {
        setLayers(r.data.layers || []);
        // Initialise state on first load only — preserve user tweaks
        // when switching conventions.
        setState((prev) => {
          if (Object.keys(prev).length) return prev;
          const next = {};
          for (const l of r.data.layers || []) {
            next[l.id] = { visible: l.visible, frozen: false, color: l.color };
          }
          return next;
        });
      })
      .catch(() => setLayers([]));
    if (onConventionChange) onConventionChange(convention);
  }, [convention, onConventionChange]);

  // ─── Apply state to the live SVG ─────────────────────────
  const applyToSvg = useCallback(() => {
    const root = svgContainerRef?.current;
    if (!root) return;
    for (const [lid, s] of Object.entries(state)) {
      const nodes = root.querySelectorAll(`[data-layer="${lid}"]`);
      nodes.forEach((n) => {
        n.style.display = s.visible ? "" : "none";
        n.style.pointerEvents = s.frozen ? "none" : "";
        if (s.color) {
          n.querySelectorAll("[stroke]").forEach((child) => {
            if (child.getAttribute("stroke") !== "none") {
              child.setAttribute("stroke", s.color);
            }
          });
        }
      });
    }
  }, [state, svgContainerRef]);

  useEffect(() => { applyToSvg(); }, [applyToSvg, layers]);

  // Re-apply when the SVG DOM changes (e.g. after Regenerate).
  useEffect(() => {
    const root = svgContainerRef?.current;
    if (!root) return;
    const obs = new MutationObserver(applyToSvg);
    obs.observe(root, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [applyToSvg, svgContainerRef]);

  const groupedLayers = useMemo(() => {
    const m = new Map();
    for (const l of layers) {
      const cat = l.category || "structural";
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat).push(l);
    }
    return CATEGORY_ORDER
      .filter((c) => m.has(c))
      .map((c) => [c, m.get(c)]);
  }, [layers]);

  const setLayer = (id, patch) =>
    setState((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const toggleCategory = (cat, visible) => {
    setState((prev) => {
      const next = { ...prev };
      for (const l of layers) {
        if ((l.category || "structural") === cat) {
          next[l.id] = { ...next[l.id], visible };
        }
      }
      return next;
    });
  };

  return (
    <div
      className="rounded-lg border border-slate-300 bg-white text-slate-900
                   shadow-sm text-xs"
      data-testid="pid-layer-panel"
    >
      <div className="flex items-center justify-between border-b
                          border-slate-200 px-3 py-2">
        <div className="font-semibold tracking-wide uppercase text-[11px]">
          Layer manager
        </div>
        <select
          data-testid="pid-layer-convention-select"
          value={convention}
          onChange={(e) => setConvention(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-0.5
                       text-[11px] focus:outline-none focus:ring-1
                       focus:ring-blue-500"
        >
          {CONVENTIONS.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="max-h-[420px] overflow-auto p-2 space-y-2">
        {groupedLayers.map(([cat, list]) => (
          <div key={cat} className="rounded border border-slate-100">
            <div className="flex items-center justify-between bg-slate-50
                                px-2 py-1">
              <div className="font-semibold text-[11px]">
                {CATEGORY_LABELS[cat] || cat}
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`pid-layer-cat-show-${cat}`}
                  onClick={() => toggleCategory(cat, true)}
                  className="text-[10px] text-blue-600 hover:underline"
                >show all</button>
                <span className="text-slate-300">|</span>
                <button
                  data-testid={`pid-layer-cat-hide-${cat}`}
                  onClick={() => toggleCategory(cat, false)}
                  className="text-[10px] text-slate-500 hover:underline"
                >hide all</button>
              </div>
            </div>
            <div>
              {list.map((l) => {
                const s = state[l.id] || { visible: true, frozen: false,
                                                  color: l.color };
                return (
                  <div
                    key={l.id}
                    className="flex items-center gap-2 px-2 py-1
                                    border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`pid-layer-row-${l.id}`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`pid-layer-visible-${l.id}`}
                      checked={s.visible}
                      onChange={(e) => setLayer(l.id,
                                                     { visible: e.target.checked })}
                      className="h-3.5 w-3.5"
                    />
                    <input
                      type="color"
                      data-testid={`pid-layer-color-${l.id}`}
                      value={s.color}
                      onChange={(e) => setLayer(l.id,
                                                     { color: e.target.value })}
                      className="h-4 w-6 cursor-pointer rounded
                                    border border-slate-300 p-0"
                    />
                    <div className="flex-1 truncate font-mono text-[11px]">
                      {l.name}
                    </div>
                    <button
                      data-testid={`pid-layer-freeze-${l.id}`}
                      onClick={() => setLayer(l.id,
                                                    { frozen: !s.frozen })}
                      className={`rounded px-1.5 text-[10px] uppercase
                                     tracking-wider ${
                        s.frozen
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {s.frozen ? "Frozen" : "Thaw"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PidLayerPanel;
